<template>
  <div class="relative feature-tree-select">
    <HLText v-if="label" as="label" variant="label" class="block mb-1 font-bold text-base">{{ label }}</HLText>
    
    <!-- Selected values display -->
    <div 
      :id="id"
      class="min-h-[38px] w-full px-3 py-2 border-2 border-black dark:border-gray-400 rounded-md bg-white dark:bg-gray-700 cursor-pointer flex items-center gap-2 flex-wrap hover:border-gray-700 dark:hover:border-gray-300 transition-colors focus-within:ring-2 focus-within:ring-primary-500"
      @click="toggleDropdown"
    >
      <span v-if="selectedFeatures.length === 0" class="text-gray-400 dark:text-gray-500 text-sm">{{ placeholder }}</span>
      <template v-else>
        <HLTag
          v-for="featureId in selectedFeatures"
          :key="featureId"
          color="primary"
          size="sm"
          class="inline-flex items-center gap-1"
        >
          {{ getFeatureLabel(featureId) }}
          <HLButton
            variant="text"
            size="2xs"
            @click.stop="removeFeature(featureId)"
            class="ml-1 -mr-1 p-0.5 h-auto min-w-0"
          >
            <Icon name="close" size="xs" />
          </HLButton>
        </HLTag>
      </template>
      <Icon 
        name="chevronDown" 
        size="sm" 
        class="ml-auto transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </div>
    
    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-96 overflow-auto"
    >
      <div class="py-2">
        <!-- Search input -->
        <div v-if="filterable" class="px-3 pb-2">
          <HLInput
            v-model="searchQuery"
            type="text"
            placeholder="Search features..."
            size="sm"
            @click.stop
          />
        </div>
        
        <!-- Feature tree -->
        <div class="space-y-0.5">
          <FeatureTreeNode
            v-for="feature in filteredRootFeatures"
            :key="feature.id"
            :feature="feature"
            :depth="0"
            :selected-features="selectedFeatures"
            :expanded-features="expandedFeatures"
            :search-query="searchQuery"
            @toggle-expand="toggleExpand"
            @toggle-select="toggleSelect"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { HLText, HLInput, HLButton, HLTag, HLIcon } from '@platform-ui/highrise'
import Icon from './Icon.vue'
import FeatureTreeNode from './FeatureTreeNode.vue'

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  features: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'All Features'
  },
  filterable: {
    type: Boolean,
    default: true
  },
  multiple: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const searchQuery = ref('')
const expandedFeatures = ref(new Set())
const dropdownRef = ref(null)

// Handle click outside to close dropdown
const handleClickOutside = (event) => {
  const selectElement = document.getElementById(props.id)
  if (isOpen.value && 
      dropdownRef.value && 
      !dropdownRef.value.contains(event.target) && 
      selectElement && 
      !selectElement.contains(event.target)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const selectedFeatures = computed({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value)
})

// Build hierarchical structure
const hierarchicalFeatures = computed(() => {
  const featureMap = new Map()
  const rootFeatures = []
  const processedChildren = new Set() // Track which features are children
  
  // Normalize features: ensure all have 'id' field
  const normalizedFeatures = props.features.map(f => ({
    ...f,
    id: f.id || f._id || f.featureId || `feature_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    parent_id: f.parent_id || f.parentId || null,
    sub_task_ids: f.sub_task_ids || []
  }))
  
  // First pass: create map of all features
  normalizedFeatures.forEach(feature => {
    featureMap.set(feature.id, {
      ...feature,
      children: []
    })
  })
  
  // Second pass: build hierarchy using parent_id
  // Note: parent_id might be either database ID or featureId, so we need to match both
  normalizedFeatures.forEach(feature => {
    if (!feature.id) return
    const featureNode = featureMap.get(feature.id)
    if (!featureNode) return
    
    const parentId = feature.parent_id || feature.parentId
    
    if (parentId) {
      // Try to find parent by database ID first
      let parent = featureMap.get(parentId)
      
      // If not found by ID, try to find by featureId
      if (!parent) {
        parent = Array.from(featureMap.values()).find(
          f => f.featureId === parentId || f.id === parentId
        )
      }
      
      if (parent && !parent.children.find(c => c.id === featureNode.id)) {
        parent.children.push(featureNode)
        processedChildren.add(featureNode.id)
      }
    }
  })
  
  // Third pass: also link features using sub_task_ids (bidirectional relationship)
  // IMPORTANT: Only link if the relationship is bidirectional (child also has parent_id pointing back)
  // Note: sub_task_ids might contain either database IDs or featureIds, so we need to match both
  normalizedFeatures.forEach(feature => {
    if (!feature.id) return
    if (feature.sub_task_ids && feature.sub_task_ids.length > 0) {
      const parentNode = featureMap.get(feature.id)
      if (parentNode) {
        feature.sub_task_ids.forEach(subTaskId => {
          // Try to find child by database ID first
          let childNode = featureMap.get(subTaskId)
          
          // If not found by ID, try to find by featureId
          if (!childNode) {
            childNode = Array.from(featureMap.values()).find(
              f => f.featureId === subTaskId || f.id === subTaskId
            )
          }
          
          if (childNode) {
            // VALIDATION: Only link if the child also has this feature as its parent_id
            // This ensures bidirectional relationships and prevents orphaned links
            const childParentId = childNode.parent_id || childNode.parentId
            const parentMatches = childParentId === feature.id || 
                                  childParentId === feature.featureId ||
                                  (feature.featureId && childParentId === feature.featureId)
            
            if (parentMatches && !parentNode.children.find(c => c.id === childNode.id)) {
              parentNode.children.push(childNode)
              processedChildren.add(childNode.id)
            }
            // Note: Orphaned relationships (child doesn't point back to parent) are silently ignored
          }
        })
      }
    }
  })
  
  // Fourth pass: add all features that aren't children to root
  normalizedFeatures.forEach(feature => {
    if (!feature.id) return
    if (!processedChildren.has(feature.id)) {
      const featureNode = featureMap.get(feature.id)
      if (featureNode && !rootFeatures.find(f => f.id === featureNode.id)) {
        rootFeatures.push(featureNode)
      }
    }
  })

  return rootFeatures
})

// Filter root features based on search
const filteredRootFeatures = computed(() => {
  if (!searchQuery.value.trim()) {
    return hierarchicalFeatures.value
  }
  
  const query = searchQuery.value.toLowerCase()
  const filterFeatures = (features) => {
    const result = []
    
    features.forEach(feature => {
      const matches = 
        (feature.featureId || '').toLowerCase().includes(query) ||
        (feature.title || '').toLowerCase().includes(query)
      
      const matchingChildren = feature.children ? filterFeatures(feature.children) : []
      
      if (matches || matchingChildren.length > 0) {
        result.push({
          ...feature,
          children: matchingChildren
        })
      }
    })
    
    return result
  }
  
  return filterFeatures(hierarchicalFeatures.value)
})

const getFeatureLabel = (featureId) => {
  const feature = props.features.find(f => f.id === featureId)
  if (!feature) return featureId
  return feature.featureId ? `${feature.featureId} ${feature.title || ''}`.trim() : (feature.title || featureId)
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const closeDropdown = () => {
  isOpen.value = false
}

const toggleExpand = (featureId) => {
  if (expandedFeatures.value.has(featureId)) {
    expandedFeatures.value.delete(featureId)
  } else {
    expandedFeatures.value.add(featureId)
  }
}

const toggleSelect = (featureId) => {
  if (props.multiple) {
    const current = [...selectedFeatures.value]
    const index = current.indexOf(featureId)
    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push(featureId)
    }
    selectedFeatures.value = current
  } else {
    selectedFeatures.value = selectedFeatures.value.includes(featureId) ? [] : [featureId]
    isOpen.value = false
  }
}

const removeFeature = (featureId) => {
  const current = [...selectedFeatures.value]
  const index = current.indexOf(featureId)
  if (index > -1) {
    current.splice(index, 1)
    selectedFeatures.value = current
  }
}

// Auto-expand when searching
watch(searchQuery, (newQuery) => {
  if (newQuery.trim()) {
    // Expand all features when searching
    props.features.forEach(f => {
      if (f.children && f.children.length > 0) {
        expandedFeatures.value.add(f.id)
      }
    })
  }
})
</script>

